import { useEffect, useMemo, useState } from 'react';

import {
  AssetDTO,
  CreateInspectionRequestDTO,
  FormElement,
  FormElementKind,
  FormType,
  GetAssetFormsByTypeResponseDTO,
  GetAssetsResponseDTO,
  GetDocumentsByAssetResponseDTO,
  GetLatestInspectionByAssetResponseDTO,
  IssueSource,
  RecordState,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { match } from 'ts-pattern';

import { AssetSelectFormElement } from '@/components/form/elements/asset-select';
import { initializeTableInitialValues } from '@/components/form/elements/table';
import { FormElementsRenderer, FormRendererData } from '@/components/form/form-elements-renderer';
import FormTemplateIcon from '@/components/icons/form-template';
import InformationIcon from '@/components/icons/information';
import PlusIcon from '@/components/icons/plus';
import RibbonAwardIcon from '@/components/icons/ribbon-award';
import SendIcon from '@/components/icons/send';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { assetKeys, getAssetsQueryOptions } from '@/queries/assets';
import { getAWADocumentsByAssetQueryOptions } from '@/queries/documents';
import { getAssetFormsByTypeQueryOptions } from '@/queries/forms';
import { getLatestInspectionQueryOptions, inspectionKeys } from '@/queries/inspections';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useAWAStore } from '@/stores/awa.store';
import { convertImageUrl } from '@/utils/image-url';

export function AWAFormPage({ formType }: { formType: FormType }) {
  const { assetId, workspaceId } = useParams();
  const navigate = useNavigate();

  const [selectedAsset, setSelectedAsset] = useState<AssetDTO | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'compliance'>('form');

  const { data: assets = [] } = useQuery({
    ...getAssetsQueryOptions({
      isAWA: true,
      filters: {
        state: RecordState.Active,
        fields: {
          ...(assetId ? { id: [assetId] } : {}),
        },
      },
    }),
    refetchOnWindowFocus: false,
    enabled: !!assetId,
  });

  useEffect(() => {
    if (assetId && assets) {
      setSelectedAsset(assets.find((asset) => asset.id === assetId) ?? null);
    }
  }, [assetId, assets]);

  const {
    data: formData,
    isFetching: isFormFetching,
    dataUpdatedAt,
  } = useQuery({
    ...getAssetFormsByTypeQueryOptions({ assetId: selectedAsset?.id ?? '', formType, isAWA: true }),
    enabled: !!selectedAsset?.id,
    refetchOnWindowFocus: false,
  });

  const { data: documentsData } = useQuery({
    ...getAWADocumentsByAssetQueryOptions({ assetId: selectedAsset?.id ?? '' }),
    enabled: !!selectedAsset?.id,
    refetchOnWindowFocus: false,
  });

  const { data: latestInspectionData, isFetching: isLatestInspectionFetching } = useQuery({
    ...getLatestInspectionQueryOptions({ assetId: selectedAsset?.id }),
    refetchOnWindowFocus: false,
  });

  const formTypeLabel = useMemo(() => {
    return (
      match(formType)
        //https://github.com/gvergnaud/ts-pattern/issues/183
        .with(FormType.InspectionForm as 0, () => 'Inspection Form')
        .with(FormType.ScheduleForm as 1, () => 'Schedule Form')
        .with(FormType.RepairForm as 2, () => 'Repair Form')
        .with(FormType.CustomForm as 3, () => 'Custom Form')
        .exhaustive()
    );
  }, [formType]);

  return (
    <div className="flex flex-col gap-4 px-4">
      <h1 className="text-center text-2xl font-bold">{formTypeLabel}</h1>
      <AssetSelectFormElement
        element={{ id: 'assetId', kind: FormElementKind.AssetSelect, label: 'Asset', required: true, config: { multiple: false } }}
        value={selectedAsset?.id ?? ''}
        onChange={() => {}}
        onChangeFull={(value) => {
          const asset = value as AssetDTO;
          if (asset) {
            if (formType === FormType.ScheduleForm || formType === FormType.RepairForm) {
              // TODO: This is a workaround to navigate to the schedule form page because the new AWA UI doesn't handle schedule forms yet
              setSelectedAsset(asset ?? null);
            } else {
              navigate(`/${workspaceId}/assets/${asset.id}`);
            }
          }
        }}
        defaultAssetId={assetId}
        readonly={isFormFetching || !!assetId}
      />
      {selectedAsset?.thumbnailUrl && (
        <img src={convertImageUrl(selectedAsset.thumbnailUrl)} alt={selectedAsset.internalId} className="h-48 w-full object-contain" />
      )}
      {selectedAsset && (
        <div>
          <ButtonStateToggle
            options={[
              { icon: <FormTemplateIcon />, label: 'Form', value: 'form' },
              { icon: <RibbonAwardIcon />, label: 'Compliance', value: 'compliance' },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>
      )}
      <div className="relative h-full">
        <div className={cn(activeTab === 'form' ? 'block' : 'hidden')}>
          {formData?.[0]?.elements && selectedAsset && !isFormFetching && !!latestInspectionData && !isLatestInspectionFetching ? (
            <AWAForm asset={selectedAsset} form={formData[0]} inspection={latestInspectionData.inspection} formDataUpdatedAt={dataUpdatedAt} />
          ) : isFormFetching || isLatestInspectionFetching ? (
            <div className="text-secondary-text mt-4 flex h-full items-center justify-center">Loading...</div>
          ) : selectedAsset ? (
            <div className="text-secondary-text mt-4 flex h-full items-center justify-center">No {formTypeLabel} assigned to this asset.</div>
          ) : (
            <div className="text-secondary-text flex h-full items-center justify-center">Select an asset to fill in the {formTypeLabel}.</div>
          )}
        </div>
        <div className={cn(activeTab === 'compliance' ? 'block' : 'hidden', 'w-full overflow-x-auto')}>
          <DocumentsTable documents={documentsData?.data ?? []} />
        </div>
      </div>
    </div>
  );
}

function AWAForm({
  asset,
  form,
  inspection,
  formDataUpdatedAt,
}: {
  asset: GetAssetsResponseDTO['data'][number];
  form: GetAssetFormsByTypeResponseDTO['forms'][number];
  inspection: GetLatestInspectionByAssetResponseDTO['inspection'];
  formDataUpdatedAt: number;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const { dailyInspectionGuardOverrideIds } = useAWAStore();

  const overrideDailyInspectionGuard = useMemo(() => {
    if (inspection) {
      return dailyInspectionGuardOverrideIds.includes(inspection.id);
    } else {
      return false;
    }
  }, [dailyInspectionGuardOverrideIds, inspection]);

  const inspectionPerformedToday = useMemo(() => {
    if (inspection) {
      return dayjs(inspection.createdAt).isSame(dayjs(), 'day');
    } else {
      return false;
    }
  }, [inspection]);

  const renderPreviousInspection = useMemo(
    () => inspectionPerformedToday && !overrideDailyInspectionGuard && form?.type === FormType.InspectionForm,
    [overrideDailyInspectionGuard, inspectionPerformedToday, form],
  );

  const formInstance = useForm<FormRendererData>({
    defaultValues: {
      ...form.elements.reduce(
        (acc, element) => ({
          ...acc,
          ...(element.kind === FormElementKind.Table
            ? {
                [element.id]: [[...initializeTableInitialValues(element)]],
              }
            : {
                [element.id]: undefined,
              }),
        }),
        {} as FormRendererData,
      ),
    },
  });

  const { mutateAsync: createInspectionForm } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!formDataUpdatedAt) {
        throw new Error("Couldn't get the form submission start time");
      }
      if (!form.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      const createInspectionResponse = await (
        await AWAApiV2Service.CreateInspectionForm({
          assetId: asset.id,
          startedAt: dayjs(formDataUpdatedAt).toISOString(),
          formRevisionId: form.latestRevisionId,
          data: Object.entries(data).map(([key, value]) => ({
            elementId: key,
            value: value === undefined || value === '' ? null : value,
            kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
          })),
        })
      ).json();

      return createInspectionResponse;
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: () => {
      toast.success('Form submitted successfully');
      queryClient.invalidateQueries({ queryKey: inspectionKeys.latestByAsset(asset.id) });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const { mutateAsync: createRepairForm } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      await AWAApiV2Service.CreateRepairForm({
        assetId: asset.id,
        formRevisionId: form.latestRevisionId,
        data: Object.entries(data).map(([key, value]) => ({
          elementId: key,
          value: value === undefined || value === '' ? null : value,
          kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
        })),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: () => {
      toast.success('Form submitted successfully');
      navigate(`/${workspaceId}/maintenance`);
    },
  });

  const { mutateAsync: createScheduleForm } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      await AWAApiV2Service.CreateScheduleFormSubmission({
        assetId: asset.id,
        formRevisionId: form.latestRevisionId,
        data: Object.entries(data).map(([key, value]) => ({
          elementId: key,
          value: value === undefined || value === '' ? null : value,
          kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
        })),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: () => {
      toast.success('Form submitted successfully');
      navigate(`/${workspaceId}/maintenance`);
    },
  });

  async function handleSubmitForm(data: FormRendererData) {
    if (form.type === FormType.InspectionForm) {
      await createInspectionForm(data);
    } else if (form.type === FormType.ScheduleForm) {
      await createScheduleForm(data);
    } else if (form.type === FormType.RepairForm) {
      await createRepairForm(data);
    }
    queryClient.invalidateQueries({ queryKey: assetKeys.all });
  }

  function handleError() {
    const formState = formInstance.formState;
    const fieldNames = Object.keys(formState.errors);
    if (fieldNames.length > 0) {
      const firstFieldWithError = document.getElementById(fieldNames[0]);
      if (firstFieldWithError) {
        firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  useEffect(() => {
    if (inspection?.data && renderPreviousInspection) {
      formInstance.reset(
        inspection.data.reduce((acc, element) => ({ ...acc, [element.elementId as FormElement['id']]: element.value }), {} as FormRendererData),
      );
    }
  }, [form, inspection?.data, formInstance, renderPreviousInspection]);

  return (
    <FormProvider {...formInstance}>
      <form onSubmit={formInstance.handleSubmit(handleSubmitForm, handleError)} className="flex flex-col gap-2">
        <InspectionPerformedTodayPanel inspection={inspection} form={form} selectedAsset={asset} overrideDailyInspectionGuard={overrideDailyInspectionGuard} />
        <FormElementsRenderer asset={asset} form={form} readonly={renderPreviousInspection} elements={form.elements} />
        <Button type="submit" disabled={renderPreviousInspection || formInstance.formState.isSubmitting}>
          {formInstance.formState.isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </form>
    </FormProvider>
  );
}

type AssetDocument = GetDocumentsByAssetResponseDTO['data'][number];

function DocumentsTable({ documents }: { documents: AssetDocument[] }) {
  const columnHelper = createColumnHelper<AssetDocument>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('scheduleType', {
        header: 'Type',
        cell: (info) => {
          const scheduleType = info.getValue();
          return scheduleType ? <Badge variant="default">{scheduleType}</Badge> : '-';
        },
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <a
            href={info.row.original.presignedDownloadUrl ?? info.row.original.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-default-blue hover:underline">
            {info.getValue()}
          </a>
        ),
      }),
      columnHelper.accessor('expiry', {
        header: 'Expiry',
        cell: (info) => {
          const date = info.getValue();
          if (!date) return <span className="text-muted">Never</span>;
          const isExpired = dayjs().isAfter(dayjs(date));
          return <span className={isExpired ? 'text-default-red' : ''}>{dayjs(date).format('DD/MM/YYYY')}</span>;
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Added',
        cell: (info) => {
          const date = info.getValue();
          return <span>{dayjs(date).format('DD/MM/YYYY')}</span>;
        },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function InspectionPerformedTodayPanel({
  inspection,
  form,
  selectedAsset,
  overrideDailyInspectionGuard,
}: {
  inspection: GetLatestInspectionByAssetResponseDTO['inspection'];
  form: GetAssetFormsByTypeResponseDTO['forms'][number];
  selectedAsset: AssetDTO;
  overrideDailyInspectionGuard: boolean;
}) {
  const { dailyInspectionGuardOverrideIds, setDailyInspectionGuardOverrideIds } = useAWAStore();
  const formInstance = useFormContext<FormRendererData>();

  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false);

  const inspectionPerformedToday = useMemo(() => {
    if (inspection && form) {
      return dayjs(inspection.createdAt).isSame(dayjs(), 'day');
    } else {
      return false;
    }
  }, [inspection, form]);

  async function handleOverrideDailyInspectionGuard() {
    if (inspection) {
      const asset = await (
        await AWAApiV2Service.GetAssets({
          limit: 100,
          filters: {
            state: RecordState.Active,
            fields: {
              ...(selectedAsset ? { id: [selectedAsset.id] } : {}),
            },
          },
        })
      ).json();

      setDailyInspectionGuardOverrideIds([...dailyInspectionGuardOverrideIds, inspection.id]);
      formInstance.reset(
        form.elements.reduce((acc, element) => {
          if (element.kind === FormElementKind.LocationSelect) {
            return {
              ...acc,
              [element.id]: (element.config?.populateLastLocation ?? true) ? asset.data[0].locationId : undefined,
            };
          }
          return {
            ...acc,
            ...(element.kind === FormElementKind.Table
              ? {
                  [element.id]: [[...initializeTableInitialValues(element)]],
                }
              : {
                  [element.id]: undefined,
                }),
          };
        }, {} as FormRendererData),
      );
    }
  }

  return (
    <>
      {inspectionPerformedToday && (
        <>
          <div className="my-2 flex flex-col items-center gap-2">
            {inspection?.createdAt && !overrideDailyInspectionGuard && form?.type === FormType.InspectionForm && (
              <div className="flex flex-col items-center gap-2">
                <Alert variant="default" className="text-center">
                  <AlertDescription className="flex items-center justify-center gap-2">
                    <InformationIcon />
                    Inspection for {dayjs(inspection.createdAt).format('DD/MM/YYYY')} completed.
                  </AlertDescription>
                </Alert>

                <ShareInspectionInput inspection={inspection} />

                <div className="flex items-center gap-2">
                  <Button variant="success" type="button" onClick={handleOverrideDailyInspectionGuard}>
                    <PlusIcon className="h-4 w-4" />
                    New inspection
                  </Button>

                  {!overrideDailyInspectionGuard && (
                    <Button variant="outline" type="button" onClick={() => setIssueDrawerOpen(true)}>
                      New Issue
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <IssueDrawer
            noTrigger
            asset={selectedAsset}
            source={IssueSource.Manual}
            open={issueDrawerOpen}
            onOpenChange={setIssueDrawerOpen}
            disableOutsideClickDismiss
            locationId={selectedAsset?.locationId ?? undefined}
          />
        </>
      )}
    </>
  );
}

function ShareInspectionInput({ inspection }: { inspection: GetLatestInspectionByAssetResponseDTO['inspection'] }) {
  const toast = useToast();

  const [email, setEmail] = useState('');

  const { mutateAsync: sendInspectionToEmail, isPending: isSendingInspectionToEmail } = useMutation({
    mutationFn: async () => {
      if (!inspection) {
        throw new Error('Inspection not found');
      }

      await AWAApiV2Service.SendInspectionToEmail({
        inspectionId: inspection.id,
        email,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: () => {
      toast.success('Inspection shared successfully');
      setEmail('');
    },
  });

  return (
    <div className="w-full">
      <Label>Share inspection:</Label>
      <Input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter email address..."
        className="w-full pr-16"
        suffix={
          <Button type="button" className="-mr-1.4 rounded-l-none" disabled={!email || isSendingInspectionToEmail} onClick={() => sendInspectionToEmail()}>
            <SendIcon />
          </Button>
        }
      />
    </div>
  );
}
